/**
 * Filename: src/data/qualifications.js
 *
 * Purpose:  Single source of truth for the Qualifications / Skills matrix and the
 *           supporting capability highlights rendered on the Home page. Keeping the
 *           content here (rather than inline in JSX) means copy edits never require
 *           touching markup, and the same data can later feed a resume export,
 *           an API response, or a print view.
 *
 * Usage:    import { skillsMatrix, capabilityHighlights, SKILL_LEVELS } from '../data/qualifications';
 *           <SkillsTable rows={skillsMatrix} highlights={capabilityHighlights} />
 */

/**
 * Canonical skill-level labels.
 * Centralized so the badge styling map in SkillsTable.js cannot drift from the data.
 */
export const SKILL_LEVELS = Object.freeze({
    TOP: 'A+++',
    EXCELLENT: 'Excellent',
    VERY_STRONG: 'Very strong',
    STRONG: 'Strong'
});

/**
 * Qualifications matrix.
 *
 * Each row:
 *   id          - stable React key (never use the array index; rows may be reordered)
 *   area        - short competency name
 *   capability  - what Scott can demonstrate in that area
 *   level       - one of SKILL_LEVELS
 *   experience  - human-readable tenure string
 */
export const skillsMatrix = [
    {
        id: 'enterprise-consulting',
        area: 'Enterprise Consulting',
        capability: 'Consulting C-Level, Directors, and technical staff',
        level: SKILL_LEVELS.EXCELLENT,
        experience: '12+ years'
    },
    {
        id: 'communication',
        area: 'Communication',
        capability: 'Explain architecture and tradeoffs to executives and engineers',
        level: SKILL_LEVELS.EXCELLENT,
        experience: '12+ years'
    },
    {
        id: 'customer-discovery',
        area: 'Customer Discovery',
        capability: 'Discover a need and turn it into an actual engineering specification',
        level: SKILL_LEVELS.EXCELLENT,
        experience: '12+ years'
    },
    {
        id: 'evaluation',
        area: 'Evaluation',
        capability: 'Determine whether an AI application actually works',
        level: SKILL_LEVELS.EXCELLENT,
        experience: '8 years'
    },
    {
        id: 'documentation',
        area: 'Documentation',
        capability: 'Expert documenter — runbooks, code, strategic memos, and training materials',
        level: SKILL_LEVELS.TOP,
        experience: '12+ years'
    },
    {
        id: 'prompt-engineering',
        area: 'Prompt Engineering',
        capability: 'Applying logic and an understanding of how LLMs construct recurrent learning sets of tasks',
        level: SKILL_LEVELS.EXCELLENT,
        experience: '6 years'
    },
    {
        id: 'data-engineering',
        area: 'Data Engineering',
        capability: 'ETL/ELT, APIs, structured and unstructured data',
        level: SKILL_LEVELS.EXCELLENT,
        experience: '12+ years'
    },
    {
        id: 'python',
        area: 'Python',
        capability: 'Front-end, back-end, scripting, and APIs',
        level: SKILL_LEVELS.VERY_STRONG,
        experience: '8 years'
    },
    {
        id: 'sql',
        area: 'SQL',
        capability: 'Expert in SQL authoring and query optimization',
        level: SKILL_LEVELS.EXCELLENT,
        experience: '12+ years'
    },
    {
        id: 'apis',
        area: 'APIs',
        capability: 'REST, authentication, and system integrations',
        level: SKILL_LEVELS.EXCELLENT,
        experience: '12+ years'
    },
    {
        id: 'llms',
        area: 'LLMs',
        capability: 'OpenAI, Anthropic, and other model provider APIs',
        level: SKILL_LEVELS.STRONG,
        experience: '6 years'
    },
    {
        id: 'rag',
        area: 'RAG',
        capability: 'Retrieval, chunking, embeddings, and vector search',
        level: SKILL_LEVELS.STRONG,
        experience: '6 years'
    },
    {
        id: 'agents',
        area: 'Agents',
        capability: 'Tool calling and multi-step agentic workflows',
        level: SKILL_LEVELS.STRONG,
        experience: '2 years'
    },
    {
        id: 'cloud',
        area: 'Cloud',
        capability: 'Azure, AWS, and GCP',
        level: SKILL_LEVELS.STRONG,
        experience: '8 years'
    },
    {
        id: 'containers',
        area: 'Containers',
        capability: 'Docker image authoring and container orchestration',
        level: SKILL_LEVELS.STRONG,
        experience: '8 years'
    },
    {
        id: 'deployment',
        area: 'Deployment',
        capability: 'CI/CD pipelines and production applications',
        level: SKILL_LEVELS.STRONG,
        experience: '8 years'
    },
    {
        id: 'full-stack',
        area: 'Full Stack',
        capability: 'Enough React / TypeScript / JavaScript to ship usable applications',
        level: SKILL_LEVELS.EXCELLENT,
        experience: '10 years'
    }
];

/**
 * Capability highlights rendered as a two-column list beneath the matrix.
 *
 * Each entry:
 *   id   - stable React key
 *   text - the highlight copy (plain string; no markup)
 */
export const capabilityHighlights = [
    { id: 'agentic-systems', text: 'Coding agentic AI systems' },
    { id: 'gans', text: 'Coding GANs (Generative Adversarial Networks) and fine tuning with internal company data' },
    { id: 'prompt-engineer', text: 'Prompt engineering' },
    { id: 'writer', text: 'Expert writer — including expert authoring of AI prompts' },
    { id: 'communicator', text: 'Expert communicator' },
    { id: 'multi-llm', text: 'Developed against a wide range of LLMs' },
    { id: 'dotnet-ai', text: 'Integrated AI technologies into existing C# .NET Core applications' },
    { id: 'scripting', text: 'Scripting in PowerShell and Bash / Linux' },
    { id: 'languages', text: 'Coding in Python, PyTorch, and C#' },
    { id: 'csharp-rest', text: 'Built C# RESTful APIs' },
    { id: 'nlp', text: 'Developed NLP and GAN applications' },
    { id: 'databricks', text: 'Big data Databricks databases' }
];
